// The read/write posture hook (Brief 5 / pt7 + pt9) — observe CONFIRMED facts
// about a session and resolve the honest posture the indicator renders. Kept
// OUT of `useChannel` so the shipped channel contract (never-lose-input,
// stream-health) is untouched; this is a thin, additive observer the session
// surfaces (SessionView / ChannelView) wire in.
//
// Honesty: a write event is recorded only from a confirmed transition,
// never an optimistic click:
//   * a real STATUS transition that a steer caused (running⇄paused/halted), via
//     `steerVerbForTransition` — the steer LANDED over presence;
//   * the bridge ATTACH moment (the hook injection) — the first genuine write —
//     recorded once when the session first becomes confirmed-attached.
// We never claim read-only while unattached; that resolves to `unknown`.
//
// The momentary write recedes on its own: a light interval ticks the clock while
// a write is fresh, so `resolvePosture` flips back to read-only after the hold
// window without the surface re-rendering on every frame.

import { useEffect, useRef, useState } from "react";
import {
  connectWrite,
  resolvePosture,
  steerVerbForTransition,
  steerWrite,
  WRITE_HOLD_MS,
  type ResolvedPosture,
  type WriteEvent,
} from "../model/readWritePosture";

export interface PostureFacts {
  /** Which session this posture is for. When it changes (the surface switches to
   * a different session without remounting — e.g. a route param change), the
   * per-session write log resets, so one session's hook-injection write + write
   * count never bleed into another's. */
  threadId: number | null;
  /** Confirmed-attached right now (the bridge is live / presence proves the
   * channel). When false the posture is `unknown` — never a claimed read-only. */
  attached: boolean;
  /** The session's TRUE status as it landed over presence ("running" | "idle" |
   * "paused" | "halted" | "needs_input" | "done" | "" before first seen). */
  status: string;
  /** The bound repo's full_name, used as the `where` for a write (the location
   * on the schematic), or "" when unbound/unknown. */
  repoFullName: string;
}

/** Observe a session's confirmed facts and return the resolved read/write
 * posture for the indicator. Pure-derived from props + an internal write log;
 * holds no transport, starts no subscription. */
export function useReadWritePosture(facts: PostureFacts): ResolvedPosture {
  const { threadId, attached, status, repoFullName } = facts;

  const [lastWrite, setLastWrite] = useState<WriteEvent | null>(null);
  const [writeCount, setWriteCount] = useState(0);
  // A clock that only advances while we need it (a fresh write is counting down).
  const [now, setNow] = useState(() => Date.now());

  // Track the previous status so we can detect the TRANSITION (not the level) —
  // a steer is a change, e.g. running → paused. The ref avoids re-running the
  // effect on its own update.
  const prevStatus = useRef<string>(status);
  // Record the hook-injection write exactly once, the first time the session is
  // confirmed-attached (a later flap doesn't re-fire "connecting bridge").
  const recordedAttach = useRef(false);

  // The session this write-log belongs to. When it changes (a surface reuses the
  // hook across sessions — a route-param swap), we reset the log so one session's
  // writes never bleed into the next.
  const trackedThread = useRef<number | null>(threadId);

  function record(event: WriteEvent) {
    setLastWrite(event);
    setWriteCount((n) => n + 1);
    setNow(Date.now());
  }

  // Reset-on-session-change + the hook-injection write, in ONE effect so they
  // can't race. Keyed on [threadId, attached]:
  //   * a NEW session clears the per-session log first (count, refs), then —
  //     because the same pass re-evaluates `attached` — records the hook
  //     injection if the new session is already attached;
  //   * the hook injection is recorded ONCE per session. A later SSE blip
  //     (live → reconnecting → live) is NOT a new hook injection (the bridge is
  //     already installed), so `recordedAttach` keeps it from re-firing — that
  //     would be a lie.
  useEffect(() => {
    if (trackedThread.current !== threadId) {
      trackedThread.current = threadId;
      prevStatus.current = "";
      recordedAttach.current = false;
      setLastWrite(null);
      setWriteCount(0);
    }
    if (attached && !recordedAttach.current) {
      recordedAttach.current = true;
      record(connectWrite(repoFullName, Date.now()));
    }
    // repoFullName intentionally omitted: the attach write is keyed on the
    // attach EDGE, not the repo name landing later; the where is best-effort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, attached]);

  // A steer that took effect — a confirmed status transition the steer caused.
  useEffect(() => {
    const prev = prevStatus.current;
    if (prev !== status) {
      const verb = steerVerbForTransition(prev, status);
      if (verb) record(steerWrite(verb, repoFullName, Date.now()));
      prevStatus.current = status;
    }
    // repoFullName is read at record time; not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Recede the momentary write: while a write is fresh, tick the clock so
  // `resolvePosture` flips back to read-only at the hold boundary. No timer once
  // the write has receded (the interval clears), so the indicator is idle at rest.
  useEffect(() => {
    if (lastWrite == null) return;
    const elapsed = Date.now() - lastWrite.at;
    if (elapsed >= WRITE_HOLD_MS) return;
    const t = setTimeout(() => setNow(Date.now()), WRITE_HOLD_MS - elapsed);
    return () => clearTimeout(t);
  }, [lastWrite]);

  return resolvePosture({ attached, lastWrite, writeCount, now });
}
